import { ref, watchEffect } from "@vue/composition-api";
import { FileData, InputFileId } from "@scrile/api-provider/dist/api/FileProvider";
import useFiles from "@/hooks/useFiles";
import { ProfileFileType } from "@/const";

interface UseProfileFileControllerOptions {
  getUserId: () => string;
  getType: () => ProfileFileType;
  onUploaded: (file: FileData) => void;
  onDeleted: () => void;
}

export default function useProfileFileController({
  getUserId,
  getType,
  onUploaded,
  onDeleted,
}: UseProfileFileControllerOptions) {
  const { uploadThenUpdate, deleteFile } = useFiles();

  const newFile = ref<File | null>(null);
  const newSrc = ref("");
  const showCropModal = ref(false);
  const loading = ref(false);
  const deleting = ref(false);

  const onUploadClick = (fileList: FileList) => {
    newFile.value = fileList[0];
    showCropModal.value = true;
  };

  const onCropClose = () => {
    showCropModal.value = false;
    newFile.value = null;
    newSrc.value = "";
  };

  const onCropped = async (file: File) => {
    try {
      showCropModal.value = false;
      loading.value = true;
      const fileId: InputFileId = {
        type: getType(),
        subjectId: getUserId(),
      };
      const newFile = await uploadThenUpdate({ fileId, file });
      onUploaded(newFile);
    } finally {
      loading.value = false;
    }
  };

  const fileReader = new FileReader();
  fileReader.onload = event => {
    if (event && event.target) {
      newSrc.value = event.target.result as string;
    }
  };

  const onDelete = async () => {
    try {
      deleting.value = true;
      await deleteFile({
        fileId: {
          subjectId: getUserId(),
          type: getType(),
        },
      });
      onDeleted();
    } finally {
      deleting.value = false;
    }
  };

  watchEffect(() => {
    if (newFile.value) {
      fileReader.readAsDataURL(newFile.value);
    }
  });

  return {
    newSrc,
    showCropModal,
    loading,
    deleting,
    onUploadClick,
    onCropClose,
    onCropped,
    onDelete,
  };
}
